import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaChalkboardTeacher, FaBookOpen, FaArrowRight, FaPlay, FaClock, FaStar } from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";

const CourseCard = ({ course, isLoggedIn = false }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const content = {
    en: {
      courseLabel: "Course",
      instructor: "Instructor",
      viewCourse: "View Course",
      continueCourse: "Continue",
      enrollNow: "Enroll Now",
      free: "Free",
      completed: "Completed",
      progress: "Progress",
      review: "Review",
      lessons: "lessons",
      rating: "Rating",
      students: "students",
      duration: "Duration",
      level: "Level",
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced"
    },
    ar: {
      courseLabel: "دورة تدريبية",
      instructor: "المدرب",
      viewCourse: "عرض الدورة",
      continueCourse: "استكمال",
      enrollNow: "سجل الآن",
      free: "مجانًا",
      completed: "مكتمل",
      progress: "التقدم",
      review: "معاينة",
      lessons: "درس",
      rating: "التقييم",
      students: "طالب",
      duration: "المدة",
      level: "المستوى",
      beginner: "مبتدئ",
      intermediate: "متوسط",
      advanced: "متقدم"
    }
  };

  const t = content[language];

  const handleViewCourse = () => {
    // Add haptic feedback simulation for Android
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    navigate(`/course/${course.id}`);
  };

  // حساب التقدم بناءً على بيانات Firebase
  const calculateProgress = () => {
    if (!course.progress) return 0;
    
    if (course.progress.percentage !== undefined) {
      return course.progress.percentage;
    }
    
    if (course.progress.completedLessons !== undefined && course.progress.totalLessons !== undefined) {
      return Math.round((course.progress.completedLessons / course.progress.totalLessons) * 100);
    }
    
    return 0;
  };

  const progressPercentage = calculateProgress();

  // الحصول على اسم المدرب من بيانات Firebase
  const getInstructorName = () => {
    if (course.instructorName) return course.instructorName;
    if (course.instructor && typeof course.instructor === 'string') return course.instructor;
    if (course.instructor && course.instructor.name) return course.instructor.name;
    return language === 'ar' ? "مدرب غير معروف" : "Unknown Instructor";
  };

  // الحصول على سعر الدورة
  const getPriceDisplay = () => {
    if (course.price === undefined || course.price === null) return null;
    return course.price === 0 ? t.free : `${course.price} EGP`;
  };

  // الحصول على الوسوم
  const getTags = () => {
    if (!course.tags) return [];
    
    if (Array.isArray(course.tags)) {
      return course.tags;
    }
    
    if (typeof course.tags === 'object') {
      return Object.values(course.tags);
    }
    
    return [];
  };

  // الحصول على مستوى الدورة
  const getLevelDisplay = () => {
    if (!course.level) return null;
    return t[course.level] || course.level;
  };

  const tags = getTags();
  const priceDisplay = getPriceDisplay();
  const instructorName = getInstructorName();
  const levelDisplay = getLevelDisplay();

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 flex flex-col h-full hover:shadow-lg transition-all cursor-pointer active:shadow-md"
      onClick={handleViewCourse}
    >
      {/* Course Image */}
      <div className="relative h-40 sm:h-44 md:h-48 overflow-hidden bg-gray-100">
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin"></div>
          </div>
        )}
        
        <img
          src={course.image || course.thumbnail || '/images/default-course-image.jpg'}
          alt={course.title}
          className={`w-full h-full object-cover transition-all duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'} ${imageError ? 'object-contain' : 'hover:scale-105'}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />

        {/* Overlay with Play Button */}
        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            whileHover={{ scale: 1, opacity: 1 }}
            className="bg-white bg-opacity-90 rounded-full p-3 shadow-lg"
          >
            <FaPlay className="text-green-700 text-lg ml-1" />
          </motion.div>
        </div>

        {/* Status Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {course.progress && progressPercentage === 100 && (
            <div className="bg-green-700 text-white text-xs px-3 py-1.5 rounded-full font-medium shadow-sm">
              {t.completed}
            </div>
          )}
          {priceDisplay && (
            <div className="bg-white text-gray-900 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
              {priceDisplay}
            </div>
          )}
        </div>

        {/* Rating Badge */}
        {course.rating && (
          <div className="absolute top-3 right-3 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <FaStar className="text-yellow-400" size={10} />
            <span>{course.rating.toFixed(1)}</span>
          </div>
        )}
      </div>

      {/* Course Content */}
      <div className="p-4 flex flex-col flex-grow">
        {/* Tags and Level */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex flex-wrap gap-1">
            {tags.length > 0 ? (
              <>
                {tags.slice(0, 1).map((tag, index) => (
                  <span key={index} className="text-xs font-medium px-2.5 py-1 bg-green-50 text-green-700 rounded-full border border-green-100">
                    {tag}
                  </span>
                ))}
                {tags.length > 1 && (
                  <span className="text-xs font-medium px-2.5 py-1 bg-gray-50 text-gray-600 rounded-full border border-gray-100">
                    +{tags.length - 1}
                  </span>
                )}
              </>
            ) : (
              <span className="text-xs font-medium px-2.5 py-1 bg-gray-50 text-gray-600 rounded-full border border-gray-100">
                {t.courseLabel}
              </span>
            )}
          </div>
          
          {levelDisplay && (
            <span className="text-xs font-medium px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-100">
              {levelDisplay}
            </span>
          )}
        </div>

        {/* Course Title */}
        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 line-clamp-2 leading-tight" style={{ minHeight: '2.5rem' }}>
          {course.title}
        </h3>

        {/* Course Meta Information */}
        <div className="space-y-2 mb-4">
          {/* Instructor */}
          <div className="flex items-center text-gray-600 text-sm">
            <FaChalkboardTeacher className={`${language === 'ar' ? 'ml-2' : 'mr-2'} opacity-70 flex-shrink-0`} />
            <span className="truncate">{instructorName}</span>
          </div>

          {/* Additional Info Row */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-3">
              {course.lessonsCount && (
                <div className="flex items-center gap-1">
                  <FaBookOpen size={10} />
                  <span>{course.lessonsCount} {t.lessons}</span>
                </div>
              )}
              
              {course.duration && (
                <div className="flex items-center gap-1">
                  <FaClock size={10} />
                  <span>{course.duration}</span>
                </div>
              )}
            </div>
            
            {course.studentsCount && (
              <span>{course.studentsCount} {t.students}</span>
            )}
          </div>
        </div>

        {/* Progress Bar - Only show if user is enrolled and has progress */}
        {course.progress && progressPercentage > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-600 mb-2">
              <span className="font-medium">{t.progress}</span>
              <span className="font-bold text-green-700">{progressPercentage}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="bg-gradient-to-r from-green-600 to-green-700 h-full rounded-full shadow-sm"
              ></motion.div>
            </div>
          </div>
        )}

        {/* Action Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={(e) => {
            e.stopPropagation();
            handleViewCourse();
          }}
          className={`mt-auto flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200 ${
            course.progress ? 
              progressPercentage === 100 ?
                'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200' :
                'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 shadow-sm hover:shadow-md' : 
              'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 shadow-sm hover:shadow-md'
          }`}
          style={{ minHeight: '44px' }} // Minimum touch target for Android
        >
          {course.progress ? 
            (progressPercentage === 100 ? 
              t.review : 
              t.continueCourse) : 
            (isLoggedIn ? t.viewCourse : t.enrollNow)}
          
          <FaArrowRight 
            className={`${language === 'ar' ? 'mr-1 transform rotate-180' : 'ml-1'} transition-transform group-hover:translate-x-1`} 
            size={12} 
          />
        </motion.button>
      </div>
    </motion.div>
  );
};

export default React.memo(CourseCard);